import {
  childSampleNumber,
  newSample,
  Sample,
  updateSampleDocument,
  updateSampleLab,
  updateSampleNumber,
} from "@/services/backend/samples/types/sample";
import { GrowthMedium } from "@/services/backend/samples/types/growth-medium";
import { Kind } from "@/services/backend/samples/types/kind";
import { v4 } from "uuid";
import { UUID } from "@bitory-gmbh/nidavellir";
import { EntityId } from "@/shared/types/entity-id";
import { Namespace } from "@/shared/types/namespace";
import { MoldDifferentiation } from "@/services/backend/samples/types/mold-differentiation";

export interface AirMicrobialSampleSet {
  id: string;
  companyId: UUID;
  documentId: EntityId;
  documentNamespace: Namespace | null;
  labId: EntityId;
  number: string;
  location: string;
  spot: string;
  comment: string;
  sampleTakers: string;
  value: string;
  unit: string;
  /**
   * The set the germ count is compared against, e.g. the outdoor air a
   * supply air is compared against; null if there is none. It is set with
   * the reference endpoint only, a PUT keeps it.
   */
  referenceSetId: EntityId;
  deviceId: EntityId;
  samplingVolume: number;
  caso: AirMicrobialSample | null;
  malt: AirMicrobialSample | null;
  dg18: AirMicrobialSample | null;
  moldDifferentiation: MoldDifferentiation[];
}

export function updateDocument(
  set: AirMicrobialSampleSet,
  id: EntityId,
  namespace: Namespace,
): AirMicrobialSampleSet {
  return {
    ...set,
    documentId: id,
    documentNamespace: namespace,
    caso: set.caso ? updateSampleDocument(set.caso, id, namespace) : null,
    malt: set.malt ? updateSampleDocument(set.malt, id, namespace) : null,
    dg18: set.dg18 ? updateSampleDocument(set.dg18, id, namespace) : null,
  };
}

export function updateSamplingVolumeInSet(
  sample: AirMicrobialSampleSet,
  samplingVolume: number,
): AirMicrobialSampleSet {
  return {
    ...sample,
    caso: sample.caso ? { ...sample.caso, samplingVolume } : sample.caso,
    malt: sample.malt ? { ...sample.malt, samplingVolume } : sample.malt,
    dg18: sample.dg18 ? { ...sample.dg18, samplingVolume } : sample.dg18,
  };
}

export function updateDeviceIdInSet(
  sample: AirMicrobialSampleSet,
  deviceId: EntityId,
): AirMicrobialSampleSet {
  return {
    ...sample,
    caso: sample.caso ? { ...sample.caso, deviceId } : sample.caso,
    malt: sample.malt ? { ...sample.malt, deviceId } : sample.malt,
    dg18: sample.dg18 ? { ...sample.dg18, deviceId } : sample.dg18,
  };
}

export function updateCreatedAtInAirSet(
  sample: AirMicrobialSampleSet,
  createdAt: string | null,
): AirMicrobialSampleSet {
  return {
    ...sample,
    caso: sample.caso ? { ...sample.caso, createdAt } : sample.caso,
    malt: sample.malt ? { ...sample.malt, createdAt } : sample.malt,
    dg18: sample.dg18 ? { ...sample.dg18, createdAt } : sample.dg18,
  };
}

export function updateAirMicrobialSetNumber(
  set: AirMicrobialSampleSet,
  number: string,
): AirMicrobialSampleSet {
  return {
    ...set,
    number,
    caso: set.caso
      ? updateSampleNumber(
          set.caso,
          childSampleNumber(number, GrowthMedium.CASO),
        )
      : null,
    malt: set.malt
      ? updateSampleNumber(
          set.malt,
          childSampleNumber(number, GrowthMedium.MALT),
        )
      : null,
    dg18: set.dg18
      ? updateSampleNumber(
          set.dg18,
          childSampleNumber(number, GrowthMedium.DG18),
        )
      : null,
  };
}

export function updateAirMicrobialSetLab(
  set: AirMicrobialSampleSet,
  labId: EntityId,
): AirMicrobialSampleSet {
  return {
    ...set,
    labId,
    caso: set.caso ? updateSampleLab(set.caso, labId) : null,
    malt: set.malt ? updateSampleLab(set.malt, labId) : null,
    dg18: set.dg18 ? updateSampleLab(set.dg18, labId) : null,
  };
}

export function updateAirMicrobialSetComment(
  set: AirMicrobialSampleSet,
  comment: string,
): AirMicrobialSampleSet {
  return {
    ...set,
    comment,
  };
}

export function updateAirMicrobialSetCaso(
  set: AirMicrobialSampleSet,
  sample: AirMicrobialSample,
): AirMicrobialSampleSet {
  if (sample.growthMedium !== GrowthMedium.CASO) {
    return set;
  }

  return {
    ...set,
    caso: sample,
  };
}

export function updateAirMicrobialSetMalt(
  set: AirMicrobialSampleSet,
  sample: AirMicrobialSample,
): AirMicrobialSampleSet {
  if (sample.growthMedium !== GrowthMedium.MALT) {
    return set;
  }

  return {
    ...set,
    malt: sample,
  };
}

export function updateAirMicrobialSetDg18(
  set: AirMicrobialSampleSet,
  sample: AirMicrobialSample,
): AirMicrobialSampleSet {
  if (sample.growthMedium !== GrowthMedium.DG18) {
    return set;
  }

  return {
    ...set,
    dg18: sample,
  };
}

/**
 * Adds the sample of the growth medium to the set, if it is missing. It
 * takes the unit and sampling volume of the samples already in the set,
 * which the base data set when the set was created.
 */
export function activateAirMicrobialSetSample(
  set: AirMicrobialSampleSet,
  growthMedium: GrowthMedium,
): AirMicrobialSampleSet {
  const sibling = set.caso ?? set.malt ?? set.dg18;
  const defaults = {
    unit: sibling?.unit ?? set.unit,
    samplingVolume: sibling?.samplingVolume ?? 0,
  };

  switch (growthMedium) {
    case GrowthMedium.CASO:
      return {
        ...set,
        caso: set.caso ?? newAirMicrobialSample(set, growthMedium, defaults),
      };
    case GrowthMedium.MALT:
      return {
        ...set,
        malt: set.malt ?? newAirMicrobialSample(set, growthMedium, defaults),
      };
    case GrowthMedium.DG18:
      return {
        ...set,
        dg18: set.dg18 ?? newAirMicrobialSample(set, growthMedium, defaults),
      };
    default:
      return set;
  }
}

export function deactivateAirMicrobialSetSample(
  set: AirMicrobialSampleSet,
  growthMedium: GrowthMedium,
): AirMicrobialSampleSet {
  switch (growthMedium) {
    case GrowthMedium.CASO:
      return {
        ...set,
        caso: null,
      };
    case GrowthMedium.MALT:
      return {
        ...set,
        malt: null,
      };
    case GrowthMedium.DG18:
      return {
        ...set,
        dg18: null,
      };
    default:
      return set;
  }
}

export function airMicrobialSampleIsActivated(
  set: AirMicrobialSampleSet,
  growthMedium: GrowthMedium,
): boolean {
  switch (growthMedium) {
    case GrowthMedium.CASO:
      return !!set.caso;
    case GrowthMedium.MALT:
      return !!set.malt;
    case GrowthMedium.DG18:
      return !!set.dg18;
    default:
      return false;
  }
}

export interface AirMicrobialSample extends Sample {
  growthMedium: GrowthMedium;
  samplingVolume: number;
  deviceId: EntityId;
  value: string;
  unit: string;
}

/**
 * Builds a sample of the set, sharing its company, document, location,
 * spot and lab. Mirrors `AirMicrobialSet.newSample` in the backend.
 */
function newAirMicrobialSample(
  set: AirMicrobialSampleSet,
  growthMedium: GrowthMedium,
  defaults: { unit: string; samplingVolume: number },
): AirMicrobialSample {
  const sample = newSample(v4(), Kind.AirMicrobial, {
    companyId: set.companyId,
    documentId: set.documentId,
    documentNamespace: set.documentNamespace,
    number: childSampleNumber(set.number, growthMedium),
    location: set.location,
    spot: set.spot,
  });

  return {
    ...sample,
    labId: set.labId,
    growthMedium,
    samplingVolume: defaults.samplingVolume,
    deviceId: null,
    value: "",
    unit: defaults.unit,
  };
}

export function updateSamplingVolume(
  sample: AirMicrobialSample,
  samplingVolume: number,
): AirMicrobialSample {
  return {
    ...sample,
    samplingVolume,
  };
}

export function updateDeviceId(
  sample: AirMicrobialSample,
  deviceId: EntityId,
): AirMicrobialSample {
  return {
    ...sample,
    deviceId,
  };
}

export function updateValue(
  sample: AirMicrobialSample,
  value: string,
): AirMicrobialSample {
  return {
    ...sample,
    value,
  };
}

/**
 * airMicrobialReferenceCandidates returns the sets the set may be compared
 * against, ordered by number. A reference is shared: any set may serve as
 * the reference of many sets, e.g. one outdoor air sample of a work order
 * for the supply air of all its air handling units.
 *
 * References never chain (the backend rejects it), so the candidates leave
 * out:
 * - the set itself,
 * - sets compared against another set, except the current reference,
 * - every set if the set is the reference of other sets itself.
 */
export function airMicrobialReferenceCandidates(
  sets: AirMicrobialSampleSet[],
  set: AirMicrobialSampleSet,
): AirMicrobialSampleSet[] {
  const isReference = sets.some((s) => s.referenceSetId === set.id);
  if (isReference) {
    return [];
  }

  return sets
    .filter((s) => s.id !== set.id)
    .filter((s) => !s.referenceSetId || s.id === set.referenceSetId)
    .sort((a, b) => a.number.localeCompare(b.number));
}

/** airMicrobialSetLabel names a set by number, sampling point and location. */
export function airMicrobialSetLabel(set: AirMicrobialSampleSet): string {
  return [set.number, set.spot, set.location].filter(Boolean).join(" · ");
}
